import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../../supabase';
import Button from '../../../components/common/Button';
import InvestmentModal from './components/InvestmentModal';
import EditIdeaModal from './components/EditIdeaModal';
import styles from './IdeaDetail.module.css';
import { Helmet } from 'react-helmet-async';
import { ICON_PATHS } from '../../../constants/iconPaths';
import JoinTeamModal from './components/JoinTeamModal';
import IdeaStatusControl from './components/IdeaStatusControl';

function IdeaDetail() {
  const { ideaId } = useParams();
  const navigate = useNavigate();
  const [idea, setIdea] = useState(null);
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isInvestModalOpen, setIsInvestModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [investments, setInvestments] = useState([]);
  const [owner, setOwner] = useState(null);
  const [members, setMembers] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [myMemberStatus, setMyMemberStatus] = useState(null);
  const [pendingMembers, setPendingMembers] = useState([]);

  useEffect(() => {
    document.body.style.backgroundColor = 'var(--gray-100)';

    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    fetchIdeaDetail();
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        fetchUserProfile();
      }
    });
  }, [ideaId]);

  useEffect(() => {
    if (ideaId) {
      fetchInvestments();
    }
  }, [ideaId]);

  useEffect(() => {
    if (idea) {
      fetchOwner();
      fetchMembers();
      fetchUniqueInvestors();
    }
  }, [idea]);

  useEffect(() => {
    if (session?.user?.id && ideaId) {
      fetchMyMemberStatus();
    }
  }, [session, ideaId]);

  useEffect(() => {
    if (idea && session?.user?.id === idea.user_id) {
      fetchPendingMembers();
    }
  }, [idea, session]);

  const fetchIdeaDetail = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('ideas')
      .select('*')
      .eq('id', ideaId)
      .single();

    if (error) {
      console.error('Error fetching idea:', error);
      return;
    }

    console.log('Fetched idea data:', data);
    console.log('All idea fields:', Object.keys(data));
    console.log('Total investment field:', data.total_investment);
    setIdea(data);
    setIsLoading(false);
  };

  const fetchUserProfile = async () => {
    try {
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('user_id, current_budget')
        .eq('user_id', session.user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }

      console.log('Fetched profile:', profile);
      setUserProfile(profile);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchInvestments = async () => {
    try {
      console.log('Fetching investments for idea:', ideaId);
      
      // 1. idea_investments 데이터를 가져옵니다
      const { data: investments, error: investmentsError } = await supabase
        .from('idea_investments')
        .select(`
          id,
          created_at,
          user_id,
          investment_amount
        `)
        .eq('idea_id', ideaId)
        .order('created_at', { ascending: false });

      if (investmentsError) {
        console.error('Error fetching investments:', investmentsError);
        return;
      }

      console.log('Fetched raw investments:', investments);

      // 2. 각 투자자의 프로필 정보를 가져옵니다
      const investmentsWithProfiles = await Promise.all(
        investments.map(async (investment) => {
          const { data: profile, error: profileError } = await supabase
            .from('user_profiles')
            .select('name')
            .eq('user_id', investment.user_id)
            .single();

          if (profileError) {
            console.error('Error fetching profile:', profileError);
            return { ...investment, investor_name: '익명의 투자자' };
          }

          return { ...investment, investor_name: profile.name };
        })
      );

      console.log('Investments with profiles:', investmentsWithProfiles);
      
      setInvestments(investmentsWithProfiles);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchOwner = async () => {
    try {
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('name')
        .eq('user_id', idea.user_id)
        .single();

      if (error) throw error;
      setOwner(profile);
    } catch (error) {
      console.error('Error fetching owner:', error);
    }
  };

  const fetchMembers = async () => {
    try {
      // 1. 승인된 멤버 목록을 가져옵니다
      const { data: members, error: membersError } = await supabase
        .from('idea_members')
        .select(`
          id,
          role,
          user_id
        `)
        .eq('idea_id', ideaId)
        .eq('status', 'approved');

      if (membersError) {
        console.error('Error fetching members:', membersError);
        return;
      }

      console.log('Fetched raw members:', members);

      // 2. 각 멤버의 프로필 정보를 가져옵니다
      const membersWithProfiles = await Promise.all(
        members.map(async (member) => {
          const { data: profile, error: profileError } = await supabase
            .from('user_profiles')
            .select('name')
            .eq('user_id', member.user_id)
            .single();

          if (profileError) {
            console.error('Error fetching profile:', profileError);
            return { ...member, user_name: '익명의 사용자' };
          }

          return { ...member, user_name: profile.name };
        })
      );

      console.log('Members with profiles:', membersWithProfiles);
      setMembers(membersWithProfiles);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchUniqueInvestors = async () => {
    try {
      console.log('Fetching investors for idea:', ideaId);
      
      // 1. idea_investments 데이터를 가져옵니다
      const { data: investments, error: investmentsError } = await supabase
        .from('idea_investments')
        .select(`
          id,
          created_at,
          user_id,
          investment_amount
        `)
        .eq('idea_id', ideaId)
        .order('created_at', { ascending: false });

      if (investmentsError) {
        console.error('Error fetching investments:', investmentsError);
        return;
      }

      console.log('Fetched raw investments:', investments);

      // 2. 중복 투자자 제거
      const uniqueUserIds = [...new Set(investments.map(inv => inv.user_id))];

      // 3. 각 투자자의 프로필 정보를 가져옵니다
      const investorsWithProfiles = await Promise.all(
        uniqueUserIds.map(async (userId) => {
          const { data: profile, error: profileError } = await supabase
            .from('user_profiles')
            .select('name')
            .eq('user_id', userId)
            .single();

          if (profileError) {
            console.error('Error fetching profile:', profileError);
            return { user_id: userId, investor_name: '익명의 투자자' };
          }

          return { user_id: userId, investor_name: profile.name };
        })
      );

      console.log('Investors with profiles:', investorsWithProfiles);
      
      setInvestors(investorsWithProfiles);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchMyMemberStatus = async () => {
    if (!session?.user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('idea_members')
        .select('status')
        .eq('idea_id', ideaId)
        .eq('user_id', session.user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116는 결과가 없는 경우
        console.error('Error fetching member status:', error);
        return;
      }

      setMyMemberStatus(data?.status);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchPendingMembers = async () => {
    try {
      // 1. 먼저 pending 상태의 멤버 신청 목록을 가져옵니다
      const { data: members, error: membersError } = await supabase
        .from('idea_members')
        .select(`
          id,
          role,
          status,
          user_id
        `)
        .eq('idea_id', ideaId)
        .eq('status', 'pending');

      if (membersError) {
        console.error('Error fetching pending members:', membersError);
        return;
      }

      console.log('Fetched raw pending members:', members);

      // 2. 각 신청자의 프로필 정보를 가져옵니다
      const membersWithProfiles = await Promise.all(
        members.map(async (member) => {
          const { data: profile, error: profileError } = await supabase
            .from('user_profiles')
            .select('name')
            .eq('user_id', member.user_id)
            .single();

          if (profileError) {
            console.error('Error fetching profile:', profileError);
            return { ...member, user_name: '익명의 사용자' };
          }

          return { ...member, user_name: profile.name };
        })
      );

      console.log('Members with profiles:', membersWithProfiles);
      setPendingMembers(membersWithProfiles);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleMembershipResponse = async (memberId, newStatus) => {
    try {
      const { error } = await supabase
        .from('idea_members')
        .update({ status: newStatus })
        .eq('id', memberId);

      if (error) throw error;

      // 상태 업데이트 후 목록 새로고침
      fetchPendingMembers();
      fetchMembers();
    } catch (error) {
      console.error('Error updating member status:', error);
      alert('처리 중 오류가 발생했습니다.');
    }
  };

  const handleOpenInvestModal = async () => {
    if (!session) {
      navigate('/admin/login', { 
        state: { redirectTo: `/project/hackathons/${ideaId}` }
      });
      return;
    }

    // 팀 멤버인 경우 투자 불가
    if (myMemberStatus === 'approved') {
      alert('팀 멤버는 자신의 아이디어에 투자할 수 없습니다.');
      return;
    }

    await fetchUserProfile();
    setIsInvestModalOpen(true);
  };

  const handleInvestSuccess = () => {
    fetchIdeaDetail();
    fetchUserProfile();
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const { error } = await supabase
        .from('ideas')
        .update({ status: newStatus })
        .eq('id', ideaId);

      if (error) throw error;
      
      fetchIdeaDetail();
    } catch (error) {
      console.error('Error updating idea status:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    }
  };

  const renderJoinButton = () => {
    if (!session) {
      return (
        <Button 
          onClick={() => navigate('/admin/login')} 
          color="secondary"
          size="medium"
        >
          로그인하고 참여하기
        </Button>
      );
    }

    if (session.user.id === idea.user_id) {
      return null;
    }

    // 아이디어 상태에 따른 버튼 렌더링
    switch (idea.status) {
      case 'team_confirmed':
        return (
          <Button 
            size="large" 
            style="secondary" 
            width="full-width"
            disabled
          >
            팀 빌딩이 완료되었습니다
          </Button>
        );
      case 'in_progress':
        return (
          <Button 
            size="large" 
            style="secondary" 
            width="full-width"
            disabled
          >
            진행중인 프로젝트입니다
          </Button>
        );
      case 'team_building':
        switch (myMemberStatus) {
          case 'pending':
            return <Button size="large" style="secondary" width="full-width">멤버 신청 완료</Button>;
          case 'approved':
            return <Button size="large" style="secondary" width="full-width">참가중</Button>;
          case 'rejected':
            return <Button size="large" style="secondary" width="full-width">거절됨</Button>;
          default:
            return (
              <Button 
                onClick={() => setIsJoinModalOpen(true)} 
                style="secondary"
                size="large"
                width="full-width"
              >
                참여하고 싶어요
              </Button>
            );
        }
      default:
        return null;
    }
  };

  const renderPendingMembersList = () => {
    // 내 아이디어가 아닌 경우 렌더링하지 않음
    if (!session || session.user.id !== idea.user_id) {
      return null;
    }

    return (
      <section className={styles.detailSection}>
        <div className={styles.sectionHeader}>  
          <h2>같이 만들고 싶어요</h2>
        </div>
        <div className={styles.participantList}>
          {pendingMembers.length > 0 ? (
            pendingMembers.map((member) => (
              <div key={member.id} className={styles.participant}>
                <div className={styles.participantInfo}>
                  <span className={styles.participantName}>
                    {member.user_name}
                  </span>
                  <span className={styles.participantRole}>
                    {member.role === 'developer' && '개발자'}
                    {member.role === 'designer' && '디자이너'}
                    {member.role === 'planner' && '기획자'}
                  </span>
                </div>
                <div className={styles.participantActions}>
                  <Button
                    style="primary"
                    size="medium"
                    onClick={() => handleMembershipResponse(member.id, 'approved')}
                  >
                    함께 하기
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className={styles.emptyMessage}>아직 신청한 리서쳐가 없어요</p>
          )}
        </div>
      </section>
    );
  };

  const renderActionButtons = () => {
    // 로그인하지 않은 경우
    if (!session) {
      return (
        <Button 
          onClick={() => navigate('/admin/login')} 
          size="large" 
          color="primary"
          width="full-width"
        >
          로그인하고 투자하기
        </Button>
      );
    }

    // 아이디어 작성자인 경우
    if (session.user.id === idea.user_id) {
      return (
        <Button 
          onClick={() => setIsEditModalOpen(true)} 
          size="large" 
          color="secondary"
          width="full-width"
        >
          수정하기
        </Button>
      );
    }

    // 팀 멤버인 경우 (approved 상태)
    if (myMemberStatus === 'approved') {
      return null; // 투자 버튼을 보여주지 않음
    }

    // 그 외의 경우 (일반 사용자)
    return (
      <Button 
        onClick={handleOpenInvestModal} 
        size="large" 
        color="primary"
        width="full-width"
      >
        투자하기
      </Button>
    );
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>로딩 중...</div>
      </div>
    );
  }

  if (!idea) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>아이디어를 찾을 수 없습니다.</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{idea?.title ? `${idea.title} - WALTER` : 'WALTER'}</title>
        <meta 
          name="description" 
          content={idea?.problem ? `${idea.problem.slice(0, 150)}...` : '기대되는 아이디어에 투자하세요.'} 
        />
      </Helmet>

      <div className={styles.container}>
        <header className={styles.header}>
            <Link to="/project/plabthon" className={styles.backButton}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d={ICON_PATHS.prev} fill="currentColor" />
              </svg>
            </Link>
          <div className={styles.headerContainer}>
            <Link to="/project/plabthon">
              <img 
                src="https://ihcmlcnktfrrojtkrtbt.supabase.co/storage/v1/object/public/asset/common/walter.svg"
                alt="Walter Logo" 
                className={styles.logo}
              />
            </Link>
          </div>
        </header>
        <div className={styles.content}>
          <section className={styles.titleSection}>
            <div className={styles.sectionHeader}>
              <h1>{idea.title}</h1>
              {session?.user?.id === idea.user_id && (
                <IdeaStatusControl
                  currentStatus={idea.status}
                  onStatusChange={handleStatusChange}
                />
              )}
            </div>
            <div className={styles.sectionBody}>
              <div className={styles.ideaInfo}>
                <p className={styles.ideaInfoTitle}>팀</p>
                <div className={styles.participantList}>
                  <span className={styles.participantName}>{owner?.name}(PO)</span>
                  {members.map((member) => (
                    <span key={member.id} className={styles.participantName}>
                      {member.user_name}
                    </span>
                  ))}
                </div>
              </div>
              <div className={styles.ideaInfo}>
                <p className={styles.ideaInfoTitle}>
                  현재 가치
                </p>
                <p className={styles.currentInvestment}>
                  {Number(idea.total_investment || 0).toLocaleString()}원
                </p>
              </div>
            </div>
          </section>

          <section className={styles.detailSection}>
              <h2>어떤 문제를 해결하고 싶나요?</h2>

            <div className={styles.detailItem}>
              <p>{idea.problem}</p>
            </div>  
            <h2>어떻게 해결하면 좋을까요?</h2>
            <div className={styles.detailItem}>
              <p>{idea.solution}</p>
            </div>
            <h2>어떤 점이 기대되나요?</h2>
            <div className={styles.detailItem}>
              <p>{idea.impact}</p>
            </div>
          </section>

          {renderPendingMembersList()}

          <section className={styles.detailSection}>
            <div className={styles.sectionHeader}>
              <h2>투자 내역</h2>
            </div>
            <div className={styles.investmentList}>
              {investments.map((investment) => (
                <div key={investment.id} className={styles.investmentItem}>
                  <div className={styles.investorInfo}>
                    <span className={styles.investorName}>
                      익명의 투자자
                    </span>
                    <span className={styles.investmentTime}>
                      {new Date(investment.created_at).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className={styles.investmentAmount}>
                    {investment.investment_amount.toLocaleString()}원
                  </div>
                </div>
              ))}
              {investments.length === 0 && (
                <div className={styles.noInvestments}>
                  아직 투자 내역이 없습니다.
                </div>
              )}
            </div>
          </section>
        </div>

        <div className={styles.actionButtonsBar}>
          <div className={styles.actionButtonsContainer}>
            {renderActionButtons()}
            {renderJoinButton()}
          </div>
        </div>

        <InvestmentModal 
          isOpen={isInvestModalOpen}
          onClose={() => setIsInvestModalOpen(false)}
          idea={idea}
          userProfile={userProfile}
          onSuccess={handleInvestSuccess}
        />

        <EditIdeaModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          idea={idea}
          onSuccess={() => {
            fetchIdeaDetail();
            setIsEditModalOpen(false);
          }}
        />

        <JoinTeamModal
          isOpen={isJoinModalOpen}
          onClose={() => setIsJoinModalOpen(false)}
          ideaId={ideaId}
          onSuccess={() => {
            fetchMyMemberStatus();
            fetchMembers();
          }}
        />
      </div>
    </>
  );
}

export default IdeaDetail; 